import { isSecretLikeKey, sanitizeAclStateForClientCache } from './redaction';

export interface PositionsAclRuntimeConfig {
  positionsAclRedactedCellEnabled: boolean;
}

export interface RuntimeConfigSource {
  positionsAclRedactedCellEnabled?: unknown;
  expiresAt?: unknown;
}

declare global {
  interface Window {
    __APP_CONFIG__?: unknown;
  }
}

const DEFAULT_CONFIG: PositionsAclRuntimeConfig = {
  positionsAclRedactedCellEnabled: false,
};

export const RUNTIME_CONFIG_URL = '/runtime-config.json';

function hasSecretLikeKey(value: unknown): boolean {
  if (Array.isArray(value)) {
    return value.some((entry) => hasSecretLikeKey(entry));
  }
  if (!value || typeof value !== 'object') {
    return false;
  }
  return Object.entries(value as Record<string, unknown>).some(
    ([key, entry]) => isSecretLikeKey(key) || hasSecretLikeKey(entry),
  );
}

function isExpired(expiresAt: unknown, nowMs: number): boolean {
  if (expiresAt === undefined || expiresAt === null) {
    return false;
  }
  if (typeof expiresAt !== 'string') {
    return true;
  }
  const expiresAtMs = Date.parse(expiresAt);
  return Number.isNaN(expiresAtMs) || expiresAtMs <= nowMs;
}

export function parsePositionsAclRuntimeConfig(
  value: unknown,
  nowMs: number = Date.now(),
): PositionsAclRuntimeConfig {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return DEFAULT_CONFIG;
  }

  if (hasSecretLikeKey(value)) {
    return DEFAULT_CONFIG;
  }

  const source = value as RuntimeConfigSource;
  if (isExpired(source.expiresAt, nowMs)) {
    return DEFAULT_CONFIG;
  }

  if (typeof source.positionsAclRedactedCellEnabled !== 'boolean') {
    return DEFAULT_CONFIG;
  }

  return {
    positionsAclRedactedCellEnabled: source.positionsAclRedactedCellEnabled,
  };
}

export function getWindowRuntimeConfig(): PositionsAclRuntimeConfig | null {
  if (typeof window === 'undefined' || window.__APP_CONFIG__ === undefined) {
    return null;
  }
  return parsePositionsAclRuntimeConfig(window.__APP_CONFIG__);
}

export function sanitizeRuntimeConfigForDebug(value: unknown): unknown {
  return sanitizeAclStateForClientCache(parsePositionsAclRuntimeConfig(value));
}

export async function loadPositionsAclRuntimeConfig(
  fetcher: typeof fetch = fetch,
): Promise<PositionsAclRuntimeConfig> {
  try {
    const response = await fetcher(RUNTIME_CONFIG_URL, {
      cache: 'no-store',
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) {
      return DEFAULT_CONFIG;
    }
    return parsePositionsAclRuntimeConfig(await response.json());
  } catch (_error) {
    return DEFAULT_CONFIG;
  }
}

